import React, { useState, useEffect } from "react";
import { useUser } from "@/providers/user-provider";
import { Button } from "../ui/buttons/button";

interface UsernameInputProps {
  onComplete: (username: string) => void;
  verifyUsername: (username: string) => Promise<boolean>;
}

export const UsernameInput: React.FC<UsernameInputProps> = ({
  onComplete,
  verifyUsername,
}) => {
  const { username: telegramUsername } = useUser();
  const [username, setUsername] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [isAvailable, setIsAvailable] = useState<boolean | null>(null);
  const [lastVerifiedName, setLastVerifiedName] = useState("");

  useEffect(() => {
    if (telegramUsername) {
      setUsername(telegramUsername);
    }
  }, [telegramUsername]);

  useEffect(() => {
    const trimmed = username.trim();
    if (trimmed.length < 3) {
      setIsAvailable(null);
      return;
    }

    if (trimmed === lastVerifiedName) return;

    const timeoutId = setTimeout(async () => {
      setIsVerifying(true);
      const available = await verifyUsername(trimmed);
      setIsAvailable(available);
      setLastVerifiedName(trimmed);
      setIsVerifying(false);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [username, verifyUsername, lastVerifiedName]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (username.trim().length >= 3 && isAvailable) {
      onComplete(username.trim());
    }
  };

  const isButtonDisabled =
    username.trim().length < 3 || isVerifying || isAvailable === false;

  return (
    <div className="w-full flex flex-col gap-4">
      <form onSubmit={handleSubmit} className="w-full flex flex-col gap-4">
        <div className="relative">
          <input
            type="text"
            placeholder="Trainer name..."
            className={`w-full bg-white/10 border-2 rounded-xl py-3 px-4 text-white text-lg font-bold text-center focus:outline-none transition-all placeholder:text-white/20 ${
              isAvailable === true
                ? "border-green-500/50 focus:border-green-500"
                : isAvailable === false
                ? "border-red-500/50 focus:border-red-500"
                : "border-white/10 focus:border-pk-blue-3"
            }`}
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            autoFocus
          />
          {username.length > 0 && username.length < 3 && (
            <p className="absolute -bottom-5 left-0 right-0 text-center text-[10px] text-red-400 font-bold uppercase tracking-wider">
              Minimum 3 characters
            </p>
          )}
          {isVerifying && (
            <p className="absolute -bottom-5 left-0 right-0 text-center text-[10px] text-pk-blue-3 font-bold uppercase tracking-wider animate-pulse">
              Verifying availability...
            </p>
          )}
          {!isVerifying && isAvailable === false && (
            <p className="absolute -bottom-5 left-0 right-0 text-center text-[10px] text-red-400 font-bold uppercase tracking-wider">
              This name is already taken
            </p>
          )}
          {!isVerifying && isAvailable === true && (
            <p className="absolute -bottom-5 left-0 right-0 text-center text-[10px] text-green-400 font-bold uppercase tracking-wider">
              Name available!
            </p>
          )}
        </div>

        <Button
          variant="default"
          type="submit"
          disabled={isButtonDisabled}
          onClick={() => void 0}
          loading={isVerifying}
          className="w-full"
        >
          <span className="text-md font-black tracking-wide px-8 py-2">
            {isVerifying ? "VERIFYING..." : "CONFIRM NAME"}
          </span>
        </Button>
      </form>
    </div>
  );
};
